import { useAuth } from './useAuth';
import { useUserPermissions } from './useUserPermissions';

// Definición de roles y sus jerarquías
export const ROLES = {
  ADMINISTRADOR: 'administrador',
  CHEF: 'chef',
  PERSONAL_COCINA: 'personal_cocina',
  CLIENTE: 'cliente',
} as const;

// Jerarquía de roles (de mayor a menor privilegio)
const ROLE_HIERARCHY: Record<string, number> = {
  [ROLES.ADMINISTRADOR]: 4,
  [ROLES.CHEF]: 3,
  [ROLES.PERSONAL_COCINA]: 2,
  [ROLES.CLIENTE]: 1,
};

// Permisos específicos por módulo
export const PERMISSIONS = {
  // Usuarios
  USUARIOS_READ: 'usuarios.read',
  USUARIOS_CREATE: 'usuarios.create',
  USUARIOS_UPDATE: 'usuarios.update',
  USUARIOS_DELETE: 'usuarios.delete',

  // Ingredientes
  INGREDIENTES_READ: 'ingredientes.read',
  INGREDIENTES_CREATE: 'ingredientes.create',
  INGREDIENTES_UPDATE: 'ingredientes.update',
  INGREDIENTES_DELETE: 'ingredientes.delete',

  // Recetas
  RECETAS_READ: 'recetas.read',
  RECETAS_CREATE: 'recetas.create',
  RECETAS_UPDATE: 'recetas.update',
  RECETAS_DELETE: 'recetas.delete',

  // Minutas
  MINUTAS_READ: 'minutas.read',
  MINUTAS_CREATE: 'minutas.create',
  MINUTAS_UPDATE: 'minutas.update',
  MINUTAS_DELETE: 'minutas.delete',

  // Clientes
  CLIENTES_READ: 'clientes.read',
  CLIENTES_CREATE: 'clientes.create',
  CLIENTES_UPDATE: 'clientes.update',
  CLIENTES_DELETE: 'clientes.delete',

  // Compras (órdenes de compra, recepciones y facturas).
  // El módulo en `modulos_sistema` se llama `compras`. Antes estas constantes
  // apuntaban a 'ordenes.*', un módulo que no existe en la BD: `hasPermission`
  // hace split('.') y busca `modulos['ordenes']`, así que devolvía false para
  // TODOS los roles, administrador incluido. No explotaba sólo porque ninguna
  // pantalla las usaba.
  COMPRAS_READ: 'compras.read',
  COMPRAS_CREATE: 'compras.create',
  COMPRAS_UPDATE: 'compras.update',
  COMPRAS_DELETE: 'compras.delete',

  // Lotes
  LOTES_READ: 'lotes.read',
  LOTES_CREATE: 'lotes.create',
  LOTES_UPDATE: 'lotes.update',
  LOTES_DELETE: 'lotes.delete',

  // Mermas
  MERMAS_READ: 'mermas.read',
  MERMAS_CREATE: 'mermas.create',
  MERMAS_UPDATE: 'mermas.update',
  MERMAS_DELETE: 'mermas.delete',

  // Proveedores
  PROVEEDORES_READ: 'proveedores.read',
  PROVEEDORES_CREATE: 'proveedores.create',
  PROVEEDORES_UPDATE: 'proveedores.update',
  PROVEEDORES_DELETE: 'proveedores.delete',

  // Dashboard
  DASHBOARD_READ: 'dashboard.read',

  // Reportes
  REPORTES_READ: 'reportes.read',
  // OJO: 'advanced' no existe en la tabla `permisos` (solo ver/crear/editar/
  // eliminar), así que hasPermission() siempre devuelve false para esta clave.
  REPORTES_ADVANCED: 'reportes.advanced',
} as const;

// Mapeo de permisos por rol
const ROLE_PERMISSIONS: Record<string, string[]> = {
  [ROLES.ADMINISTRADOR]:
    // Acceso total a todo
    Object.values(PERMISSIONS),
  [ROLES.CHEF]: [
    // Ingredientes: acceso completo
    PERMISSIONS.INGREDIENTES_READ,
    PERMISSIONS.INGREDIENTES_CREATE,
    PERMISSIONS.INGREDIENTES_UPDATE,
    PERMISSIONS.INGREDIENTES_DELETE,

    // Recetas: acceso completo
    PERMISSIONS.RECETAS_READ,
    PERMISSIONS.RECETAS_CREATE,
    PERMISSIONS.RECETAS_UPDATE,
    PERMISSIONS.RECETAS_DELETE,

    // Minutas: acceso completo
    PERMISSIONS.MINUTAS_READ,
    PERMISSIONS.MINUTAS_CREATE,
    PERMISSIONS.MINUTAS_UPDATE,
    PERMISSIONS.MINUTAS_DELETE,

    // Compras: acceso completo
    PERMISSIONS.COMPRAS_READ,
    PERMISSIONS.COMPRAS_CREATE,
    PERMISSIONS.COMPRAS_UPDATE,
    PERMISSIONS.COMPRAS_DELETE,

    // Lotes y mermas: acceso completo
    PERMISSIONS.LOTES_READ,
    PERMISSIONS.LOTES_CREATE,
    PERMISSIONS.LOTES_UPDATE,
    PERMISSIONS.LOTES_DELETE,
    PERMISSIONS.MERMAS_READ,
    PERMISSIONS.MERMAS_CREATE,
    PERMISSIONS.MERMAS_UPDATE,
    PERMISSIONS.MERMAS_DELETE,

    // Clientes: solo lectura
    PERMISSIONS.CLIENTES_READ,

    // Reportes: acceso avanzado
    PERMISSIONS.REPORTES_READ,
    PERMISSIONS.REPORTES_ADVANCED,

    // Usuarios: solo lectura
    PERMISSIONS.USUARIOS_READ,
  ],
  [ROLES.PERSONAL_COCINA]: [
    // Ingredientes: lectura y actualización de stock
    PERMISSIONS.INGREDIENTES_READ,
    PERMISSIONS.INGREDIENTES_UPDATE,

    // Recetas: solo lectura
    PERMISSIONS.RECETAS_READ,

    // Minutas: lectura y actualización
    PERMISSIONS.MINUTAS_READ,
    PERMISSIONS.MINUTAS_UPDATE,

    // Compras: lectura y creación
    PERMISSIONS.COMPRAS_READ,
    PERMISSIONS.COMPRAS_CREATE,

    // Reportes: solo lectura básica
    PERMISSIONS.REPORTES_READ,
  ],
  [ROLES.CLIENTE]: [
    // Solo puede ver sus propios pedidos y minutas disponibles
    PERMISSIONS.MINUTAS_READ,
    PERMISSIONS.CLIENTES_READ, // Solo sus propios datos
  ],
};

export interface UsePermissionsReturn {
  // Verificación de roles
  hasRole: (role: string | string[]) => boolean;
  isAdmin: boolean;
  isChef: boolean;
  isPersonalCocina: boolean;
  isCliente: boolean;

  // Verificación de permisos específicos
  hasPermission: (permission: string | string[]) => boolean;

  // Verificación de jerarquía de roles
  hasRoleOrHigher: (minimumRole: string) => boolean;

  // Información del usuario
  currentRole: string | null;
  userName: string | null;
  userId: number | null;
  permissionsLoading: boolean;
}

export const usePermissions = (): UsePermissionsReturn => {
  const { user, isAuthenticated } = useAuth();
  const { data: userPermissions, isLoading: permissionsLoadingRaw } =
    useUserPermissions();

  const currentRole = user?.rol || null;
  const userName = user?.nombre || null;
  const userId = user?.id || null;

  // Solo marcar como loading si está autenticado Y cargando permisos
  // Si no está autenticado, no necesitamos cargar permisos
  const permissionsLoading = isAuthenticated ? permissionsLoadingRaw : false;

  console.log('🎭 [usePermissions] Estado de permisos:', {
    isAuthenticated,
    permissionsLoadingRaw,
    permissionsLoading,
    hasUser: !!user,
    currentRole,
  });

  // Verificar si tiene un rol específico
  const hasRole = (role: string | string[]): boolean => {
    if (!currentRole) return false;

    if (Array.isArray(role)) {
      return role.includes(currentRole);
    }

    return currentRole === role;
  };

  // Verificar si tiene un rol o uno superior en la jerarquía
  const hasRoleOrHigher = (minimumRole: string): boolean => {
    if (!currentRole) return false;

    const currentLevel = ROLE_HIERARCHY[currentRole] || 0;
    const minimumLevel = ROLE_HIERARCHY[minimumRole] || 0;

    return currentLevel >= minimumLevel;
  };

  // Verificar permisos específicos
  // Prioriza permisos dinámicos de la API, con fallback a permisos hardcodeados
  const hasPermission = (permission: string | string[]): boolean => {
    if (!currentRole) return false;

    // Si tenemos permisos dinámicos desde la API, usarlos
    if (userPermissions && userPermissions.modulos) {
      const permissions = Array.isArray(permission) ? permission : [permission];

      return permissions.some((p) => {
        // Formato esperado: 'modulo.permiso' (ej: 'ingredientes.read', 'recetas.create')
        const [moduleName, action] = p.split('.');
        const moduloData = userPermissions.modulos[moduleName];

        if (!moduloData || !moduloData.permisos) return false;

        // Mapear acciones a permisos
        switch (action) {
          case 'read':
            return moduloData.permisos.ver;
          case 'create':
            return moduloData.permisos.crear;
          case 'update':
            return moduloData.permisos.editar;
          case 'delete':
            return moduloData.permisos.eliminar;
          default:
            return false;
        }
      });
    } // Fallback a permisos hardcodeados para roles base
    const rolePermissions = ROLE_PERMISSIONS[currentRole] || [];

    if (Array.isArray(permission)) {
      return permission.some((p) => rolePermissions.includes(p));
    }

    return rolePermissions.includes(permission);
  };

  // Atajos para roles comunes
  const isAdmin = hasRole(ROLES.ADMINISTRADOR);
  const isChef = hasRole(ROLES.CHEF);
  const isPersonalCocina = hasRole(ROLES.PERSONAL_COCINA);
  const isCliente = hasRole(ROLES.CLIENTE);

  return {
    hasRole,
    hasRoleOrHigher,
    hasPermission,
    isAdmin,
    isChef,
    isPersonalCocina,
    isCliente,
    currentRole,
    userName,
    userId,
    permissionsLoading,
  };
};

export default usePermissions;
